import React, { FC, ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import '../../src/tooltip.css';
import TableTab from '../components/tabs/table-tab';
import { toast } from "react-toastify";
import { CUSTOM_VALUES, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from '../constants/command-status';
import { DEF_PG_SIZE_TVIEWER } from '../constants/maximum-page-size';
import { useLocale } from '../contexts/localeContext';
import { TranslationKeys } from '../contexts/TranslationKeys';
import alarmBellIcon from '../images/AlarmBell.svg';
import ackAlarmBellIcon from '../images/accounts_ticks_icon.svg';
import AlarmBellHighIcon from '../images/AlarmBellHigh.svg';
import ControllerIcon from '../images/Controller.svg';
import ReaderIcon from '../images/Reader.svg';
import UserDefaultProfile from '../images/UserProfileDefault.svg';
import { Category } from '../models/Category';
import columns from '../models/ColumnDefinitions/AlarmViewerColumns';
import { ListQuery } from '../models/ListQuery';
import { Lookup } from '../models/Lookup';
import CategorySearch from '../models/search/CategorySearch';
import DeviceImproxSearch from '../models/search/DeviceImproxSearch';
import DoorSearch from '../models/search/DoorSearch';
import SiteSearch from '../models/search/SiteSearch';
import searchColumns from '../models/search/AlarmViewerSearch';
import ZoneSearch from '../models/search/ZoneSearch';
import { TransactionViewer } from '../models/TransactionViewer';
import '../pages/access-control/monitoring/transaction-event.css';
import { getDeviceImproxNames } from '../services/deviceImproxService';
import { getDoorNamesWithPagination } from '../services/doorService';
import { getPersonByID } from '../services/enrolmentService';
import { getAllEnrolmentProfileNames } from '../services/profileService';
import { getSiteNamesWithPagination } from '../services/siteService';
import { getZoneNamesWithPagination } from '../services/zoneService';
import { IAppState } from '../store/types';
import Tooltip from '../components/Tooltip/Tooltip';
import { MONITORING_ALARMS } from '../constants/menu-types';
import { TableContextProvider } from '../contexts/table/TableContextProvider';
import { useIcaWebsocketContext } from '../contexts/websocket/IcaWebsocketContext';
import { ListData } from '../models/ListData';
import { TableLegendItem } from '../models/table/TableLegendItem';
import { showError } from '../services/utilities/ServiceError';
import { showTagCode } from '../utilities/Permissions';
import { getEventTypeNamesWithPagination } from '../services/eventTypeService';
import EventTypeSearch from '../models/search/EventTypeSearch';
import { LICENSE_APERIO_OSS } from '@/constants/license-constants';
import { useGlobalState } from '@/Providers';
import { hideColumns } from '@/models/utilities/columnHelper';
import OssDeviceSearch from '@/models/search/OssDeviceSearch';
import { getOssDeviceNamesWithPagination } from '@/services/ossDeviceService';

interface AlarmsViewerTabProps {
    categories: Category[];
    setCategories: (categories: Category[]) => void;
    selectedIds: string[];
    setSelectedIds: (selectedIds: string[]) => void;
    setAcknowledgedAlarmIds: React.Dispatch<React.SetStateAction<Set<string>>>;
}

interface Thumbnail {
    imageURL: any;
    event: string;
    tooltipText: string;
    dateTime: string;
}

export const eventStatusFilterKey = 'status';
export const eventCategories = {
    standard: { value: 'standard', id: "1" },
    acknowledgedAlarm: { value: 'acknowledged', id: "2" },
    highAlarm: { value: 'high', id: "3" },
};

const AlarmsViewerTab: FC<AlarmsViewerTabProps> = ({categories, setCategories, selectedIds, setSelectedIds, setAcknowledgedAlarmIds }) => {
    const { translate } = useLocale();
    let hasMounted = false;
    const title = translate.byKey(TranslationKeys.alarms);
    const [rows, setRows] = useState<TransactionViewer[]>([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [hasReceivedFirstMessage, setHasReceivedFirstMessage] = useState(false);
    let oldRows: TransactionViewer[] = [];
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [eventHistoryThumbnails, setEventHistoryThumbnails] = useState<ReactElement[]>();
    const lastFiveImages: Thumbnail[] = [];
    const [eventLegend] = useState<TableLegendItem[]>([
        {
            value: eventCategories.highAlarm.id,
            imageIcon: AlarmBellHighIcon,
            label: translate.byKey(TranslationKeys.high),
            tooltip: '',
            isSelected: true
        },
        {
            value: eventCategories.standard.id,
            imageIcon: alarmBellIcon,
            label: translate.byKey(TranslationKeys.standard),
            tooltip: '',
            isSelected: true
        },
        {
            value: eventCategories.acknowledgedAlarm.id,
            imageIcon: ackAlarmBellIcon,
            label: translate.byKey(TranslationKeys.acknowledged),
            tooltip: '',
            isSelected: true
        }
    ]);
    const icaWebSocket = useIcaWebsocketContext();
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = MONITORING_ALARMS;
    const lookup: Lookup[] = [
        {
            name: 'site',
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'door',
            values: [],
            dataMethod: getDoorNamesWithPagination,
            searchAccessor: DoorSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'zone',
            values: [],
            dataMethod: getZoneNamesWithPagination,
            searchAccessor: ZoneSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'category',
            values: [],
            dataMethod: getAllEnrolmentProfileNames,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'device',
            values: [],
            dataMethod: getDeviceImproxNames,
            searchAccessor: DeviceImproxSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'event',
            values: [],
            dataMethod: getEventTypeNamesWithPagination,
            searchAccessor: EventTypeSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'ossDevice',
            values: [],
            dataMethod: getOssDeviceNamesWithPagination,
            searchAccessor: OssDeviceSearch.find(item => item.accessor === "name")?.searchOnName
        }
    ];
    const { buildInfo } = useGlobalState() ?? {};
    const isLicensedForAperioOss = buildInfo?.licenceIds?.includes(LICENSE_APERIO_OSS);

    useEffect(() => {
        if (icaWebSocket.connectionState) {
            // Stop any lingering topics from previous views (device states, tasks, discovery, etc.)
            // but keep MONITORING_ENGINE running since the menu widget always needs it.
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DEVICE, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TASKS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DISCOVERY, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_ENROL, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_QUEUE, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);

            // Now start only the transactions topic for this view (with alarms-only filter)
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TRANSACTIONS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START, CUSTOM_VALUES.ALARMS_ONLY);
            icaWebSocket.setOnMessage(onMessageReceived);

            // Safety fallback: if the websocket connects but no initial message arrives within
            // 15 seconds (e.g. no recent alarms), clear the spinner so the user sees the
            // "Waiting for first update" message rather than being stuck on a loading indicator.
            // Note: We only hide the spinner here, NOT mark that we've received a message.
            // The message will persist until an actual websocket message arrives.
            const fallbackTimer = setTimeout(() => {
                setIsInitialLoad(false);
            }, 15000);
            return () => {
                clearTimeout(fallbackTimer);
                icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TRANSACTIONS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
            }
        }

        return () => {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TRANSACTIONS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
        }
    }, [icaWebSocket.connectionState]);

    const mapEventCategoryToStatus = (event: any) => {
        const isAcknowledged = !!event.attributes.subject.ackBy?.attributes?.dateTime;

        if (isAcknowledged) {
            setAcknowledgedAlarmIds(prev => new Set(prev).add(event.attributes.id));
            return eventCategories.acknowledgedAlarm.id;
        }
        else if (event.attributes.subject.event.attributes.eventPriorityLevel === '1') {
            return eventCategories.highAlarm.id;
        }
        else {
            return eventCategories.standard.id;
        }
    };

    const onMessageReceived = async (data) => {
        // Clear the initial-load spinner on the first websocket message, regardless
        // of whether it carries alarms. This matches the UX contract: show the
        // spinner while waiting for the server's initial response, then hide it.
        setIsInitialLoad(false);
        setHasReceivedFirstMessage(true);
        try {
            const response = JSON.parse(data.data);
            if (response.command === MONITORING_COMMAND.MONITORING_TRANSACTIONS) {

                response.events.forEach((event) => {
                    const eventCategory = event.attributes.subject.event.attributes.eventCategory.toLowerCase();

                    if (eventCategory !== 'alarm') {
                        return;
                    }

                    const statusId = mapEventCategoryToStatus(event);
                    const dateTime = event.attributes.timestampLocal.replace("T", " ").replace(".000Z", "").replace(".000", "");
                    const eventId = event.attributes.subject.event.key.eventTypeId;
                    const acknowledgedBy = event.attributes.subject.ackBy?.attributes?.displayName || '-';

                    const transaction: TransactionViewer = {
                        id: event.attributes.id,
                        name: event.attributes.subject.event.attributes.nameOriginal,
                        tagCode: event.attributes.subject.credential ? showTagCode(user.profileId, event.attributes.subject.credential.attributes.tagcode) : '-',
                        dateTime: dateTime,
                        event: event.attributes.subject.event.attributes.nameOriginal,
                        eventId: eventId,
                        eventCategory: eventCategory,
                        device: event.attributes.source.device ? event.attributes.source.device.attributes.name : "-",
                        acknowledgedBy: acknowledgedBy,
                        site: event.attributes.subject.site.attributes.siteName,
                        credentialHolder: event.attributes.subject.credentialHolder ? event.attributes.subject.credentialHolder.attributes.mstDisplayname : '-',
                        deviceId: event.attributes.source.device ? event.attributes.source.device.key.deviceId : '-',
                        siteId: event.attributes.subject.site.attributes.siteId,
                        door: event.attributes.subject.door ? event.attributes.subject.door.attributes.doorName : '-',
                        doorId: event.attributes.subject.door ? event.attributes.subject.door.attributes.doorId : '-',
                        zone: event.attributes.subject.zone ? event.attributes.subject.zone.attributes.zoneName : '-',
                        zoneId: event.attributes.subject.zone ? event.attributes.subject.zone.attributes.zoneId : '-',
                        category: event.attributes.subject.credentialHolder ? event.attributes.subject.credentialHolder.attributes.enrolmentCategory.attributes.name : '-',
                        categoryId: event.attributes.subject.credentialHolder ? event.attributes.subject.credentialHolder.attributes.enrolmentCategory.key.profileId : '-',
                        status: statusId.toString(),
                        credentialHolderId: event.attributes.subject.credentialHolder ? event.attributes.subject.credentialHolder.key.masterId : '-',
                        reader: event.attributes.subject.reader ? event.attributes.subject.reader.attributes.name : '-',
                        controller: event.attributes.subject.controller ? event.attributes.subject.controller.attributes.name : '-',
                        ossDevice: event.attributes.subject.ossDevice ? event.attributes.subject.ossDevice.attributes.name : '-',
                        ossDeviceId: event.attributes.subject.ossDevice ? event.attributes.subject.ossDevice.key.deviceId : '-'
                    }

                    const eventAlreadyExists = oldRows.some(row => row.id === transaction.id);

                    if (eventAlreadyExists) {
                        return;
                    }
                    oldRows.unshift(transaction);
                    // trim off the last element if alarm rows are more than 100
                    if (oldRows.length > 100) {
                        oldRows.pop();
                    }
                })

                setCategories([{ ...categories[0], count: oldRows.length }]);
                setRows([...oldRows]);
            }
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    useEffect(() => {
        dataRefresh((listParameters?: ListQuery) => getAlarms(listParameters));
    }, [rows])

    const loadAlarmImage = (alarm: TransactionViewer) => {
        // Alarm for a known person
        if (alarm.credentialHolder !== '-') {
            setCredentialImage(alarm.credentialHolderId, alarm.eventCategory, alarm.dateTime);
        }
        // Event originated from a reader
        else if (alarm.reader !== '-') {
            setLastFiveImages(alarm.eventCategory, ReaderIcon, alarm.reader, alarm.dateTime);
        }
        // Event originated from a controller
        else {
            setLastFiveImages(alarm.eventCategory, ControllerIcon, alarm.controller, alarm.dateTime)
        }
    }

    const loadImages = (oldRows) => {
        oldRows.forEach((event) => {
            loadAlarmImage(event);
        })
    }

    const getAlarms = (listParameters?: ListQuery): Promise<ListData> => {
        let modifiedRows = rows.filter(item => item[listParameters?.searchTerm?.accessor ?? 'event'].toUpperCase().includes((listParameters?.searchTerm?.value ?? '').toUpperCase()));
        if (hasMounted === false && modifiedRows.length === 0) {
            modifiedRows = oldRows;
            hasMounted = true;
        }

        const allFilters = [...listParameters?.filters ?? []];
        const selectedFilter: { key: string, selectedOptions: string[] }[] = allFilters.map(filter => {
            return { key: filter.key, selectedOptions: filter.selected.map(selectedFilter => { return selectedFilter.value }) }
        });

        modifiedRows = modifiedRows.filter(row => {
            let isMatch = true;

            if (selectedFilter.length !== 0) {
                selectedFilter.forEach(filter => {
                    if (filter.selectedOptions.length > 0) {
                        const value = row[filter.key].toString();
                        isMatch = filter.selectedOptions.includes(value) && isMatch;
                    }
                });
            }

            return isMatch;
        });

        // Show latest transactions top of the list
        modifiedRows.sort((a: any, b: any) => {
            return b.dateTime > a.dateTime ? 1 : -1;
        });

        lastFiveImages.splice(0, lastFiveImages.length);
        loadImages(modifiedRows.slice(0, 5))

        return new Promise((resolve, reject) => {
            return resolve({
                rows: modifiedRows,
                pagination: { maxPageSize: DEF_PG_SIZE_TVIEWER, pageNumber: 1, totalCount: modifiedRows.length, totalPages: 1 }
            });
        })
    }

    const setLastFiveImages = (eventCategory: string, imageURL: string, tooltipText: string, dateTime?) => {
        if (lastFiveImages.length === 5) {
            lastFiveImages.splice(0, 1);
            lastFiveImages.push({ imageURL: imageURL, event: eventCategory, tooltipText: tooltipText, dateTime });
        }
        else {
            lastFiveImages.push({ imageURL: imageURL, event: eventCategory, tooltipText: tooltipText, dateTime });
        }

        lastFiveImages.sort((a, b) => {
            const dateA = new Date(a.dateTime).getTime();
            const dateB = new Date(b.dateTime).getTime();
            return dateB - dateA;
        });

        setEventHistoryThumbnails(
            lastFiveImages.map((imageContent, index) => {
                return <div className="mr-3" key={`eventHistoryImages${index}`}>
                    <Tooltip show text={imageContent.tooltipText} direction="bottom">
                        <img src={imageContent.imageURL} className='object-contain' style={{ width: '80px', height: '56px' }} alt="1" />
                    </Tooltip>
                    <hr className={`h-1 mt-1 flex-grow ${imageContent.event.toLowerCase()}`} />
                </div>
            })
        )
    }

    const setCredentialImage = async (masterId: string, eventCategory: string, alarmId?: string) => {
        try {
            const person = await getPersonByID(translate, masterId);

            person.masterImages?.length
                ? setLastFiveImages(eventCategory, `data:image/png;base64,${person.masterImages[0].base64Thumb}`, person.name, alarmId)
                : setLastFiveImages(eventCategory, UserDefaultProfile, person.name, alarmId);
        } catch (error) {
            // TODO: Commenting out as causes a spam of toasts when a site filter is applied on the viewing operator
            // showError(error, toast.warn);
        }
    }

    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }

    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail) => void) => {
        setDetails(userLoginId, currentMenuDetail);
    }

    const emptyMessage = !hasReceivedFirstMessage ? translate.byKey(TranslationKeys.waitingForFirstUpdate) : undefined;

    return <TableContextProvider
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        dataMethod={getAlarms}
        externalBusy={isInitialLoad}
        tableDescriptor={isLicensedForAperioOss ?  columns : hideColumns(columns, ['ossDevice'])}
        searchDescriptor={searchColumns}
        legendItems={eventLegend}
        useTable
        useFilter
        useSearch
        legendKey={eventStatusFilterKey}
        manualDataRefresh={manualDataRefresh}
        lookup={lookup}
        showAddNewButton={false}
        emptyStateMessage={emptyMessage}>
        <TableTab
            title={title}
            selectRows={true}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            isPaginationSupported={false}
            eventThumbnails={eventHistoryThumbnails}
            tableHeight="490px"
            overflowScroll  
            cyName="alarmsViewer" />
    </TableContextProvider>
}

export default AlarmsViewerTab;